import { useEffect } from "react";

import { Redirect, useLocation } from "react-router-dom";

import { AccountOnHoldCheckoutMessage } from "@/components/AccountOnHold";
import { useAppState } from "@/components/AppStateProvider";
import Grid from "@/components/Grid";
import Loading from "@/components/Loading";
import ReportListCard from "@/components/ReportListCard";
import { useLocalesContext } from "@/contexts/Locale";
import { useTestProductModalContext } from "@/contexts/TestProductModalContext";
import { useOrganisationBasketContext } from "@/contexts/organisations/OrganisationBasketContext";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { COMING_SOON, TEST_PRODUCT_LISTING_CMP_REPORTS } from "@/core/constants";
import { ERROR_404_URL } from "@/core/urls";
import {
  PRACTITIONER_TEST_PRODUCT_CONNECTIONS_QUERY,
  PRACTITIONER_TEST_PRODUCT_DETAIL_QUERY
} from "@/graphql/shop";
import useUserProfile from "@/hooks/useUserProfile";
import { PanelBoxV2 } from "@/tpo/Boxes";
import InfiniteScroll, { InfiniteScrollContext } from "@/tpo/InfiniteScroll";
import Spacer from "@/tpo/Spacer";
import ListPageTemplate from "@/tpo/shop/ListPageTemplate";
import useTestProducts from "@/tpo/shop/hooks/useTestProducts";
import { useLazyQuery } from "@apollo/client";
import { useTheme } from "styled-components";

function ReportsListContent() {
  const theme = useTheme();

  const { locale } = useLocalesContext();
  const { organisation } = useOrganisationContext();

  const { basket } = useOrganisationBasketContext();

  const {
    products: { items, hasMore, endCursor },
    loading,
    fetchMore
  } = useTestProducts({
    query: PRACTITIONER_TEST_PRODUCT_CONNECTIONS_QUERY,
    queryVariables: {
      organisation: organisation?.id,
      listingVisibility: TEST_PRODUCT_LISTING_CMP_REPORTS
    },
    connectionsFieldName: "practitionerTestProductConnections",
    validatedFilters: {
      gender: "all",
      categories: [],
      sampleTypes: [],
      testingServices: []
    },
    search: "",
    sort: "availability_order,sort_order",
    isoCode: locale?.isoCode,
    paymentIsoCode: basket?.paymentLocale?.isoCode,
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only"
  });

  const { selectTestProductId } = useTestProductModalContext();

  const infiniteScrollContent = (
    <InfiniteScrollContext.Consumer>
      {({ itemsList, setBottomElement }) => (
        <>
          {itemsList.map((testProduct, idx) => {
            return (
              <ReportListCard
                key={testProduct.id}
                id={testProduct.id}
                name={testProduct.name}
                shortDescription={testProduct.content.shortDescription}
                iconType={testProduct.content.reportIcon}
                iconSize={[40, 40, 80]}
                onClick={() => {
                  if (testProduct.status === COMING_SOON) {
                    return;
                  }
                  selectTestProductId(testProduct.id);
                }}
                ref={idx === itemsList.length - 1 ? setBottomElement : null}
                functionalDatapointCount={testProduct.functionalDatapointCount}
                dnaDatapointCount={testProduct.dnaDatapointCount}
                symptomsCount={testProduct.questionsCount}
                status={testProduct.status}
                cursor={testProduct.status === COMING_SOON ? "default" : "pointer"}
                showDescription={testProduct.status !== COMING_SOON}
                showMarkers={testProduct.status !== COMING_SOON}
                topRightButton={null}
              />
            );
          })}
        </>
      )}
    </InfiniteScrollContext.Consumer>
  );

  const content = (
    <Grid gridTemplateColumns={["repeat(1, 1fr)", "repeat(2, 1fr)", "repeat(2, 1fr)"]} gap={16}>
      {infiniteScrollContent}
    </Grid>
  );

  return (
    <ListPageTemplate
      urlMap={{
        supplements: `/cmp/organisations/${organisation.hashId}/dashboard/shop/supplements`,
        tests: `/cmp/organisations/${organisation.hashId}/dashboard/shop/tests`,
        reports: `/cmp/organisations/${organisation.hashId}/dashboard/shop/reports`,
        discounts: `/cmp/organisations/${organisation.hashId}/dashboard/shop/discounts`
      }}
      switcherOptions={[
        {
          label: "tests",
          value: "tests"
        },
        {
          label: "reports",
          value: "reports"
        },
        {
          label: "supplements",
          value: "supplements"
        },
        {
          label: "discounts",
          value: "discounts"
        }
      ]}
      bg="haze"
      tab="reports"
      title="Unlock the power of reports"
      subtitle="Our reports give you a powerful way to understand your patient’s health by combining their DNA insights with relevant functional markers. Each report turns complex data into clear, clinically focused stories that help you spot patterns faster, guide more targeted interventions, and deliver a more personalised standard of care."
      pb={[30, 30, 60]}
      centerProps={{
        stacked: true
      }}
      aboveSwitcher={
        organisation?.accountOnHold ? (
          <AccountOnHoldCheckoutMessage withGreyOuterBox={false} maxWidth={640} mb={60} />
        ) : null
      }
    >
      <PanelBoxV2
        maxWidth={1280}
        outer={{
          bg: "haze",
          pb: theme.spacing.section.pb,
          px: [20, 20, 40]
        }}
      >
        <Spacer py={[2, 2, 20]} />
        <InfiniteScroll
          loader={<Loading />}
          hasMore={hasMore}
          loading={loading}
          next={() => {
            if (endCursor) {
              fetchMore({
                variables: {
                  after: endCursor,
                  first: 10
                }
              });
            }
          }}
          items={items}
        >
          {content}
        </InfiniteScroll>
      </PanelBoxV2>
    </ListPageTemplate>
  );
}

export default function ReportsListPage() {
  const location = useLocation();
  const slug = new URLSearchParams(location.search).get("slug") || "";

  const { practitionerProfile, loading: profileLoading } = useUserProfile();
  const { selectTestProductId } = useTestProductModalContext();
  const { basket } = useOrganisationBasketContext();
  const { organisation } = useOrganisationContext();
  const { basketOpen } = useAppState();

  const [fetchProduct] = useLazyQuery(PRACTITIONER_TEST_PRODUCT_DETAIL_QUERY, {
    onCompleted: data => {
      if (
        data?.practitionerTestProduct?.id &&
        data?.practitionerTestProduct?.status !== COMING_SOON &&
        !basketOpen
      ) {
        selectTestProductId(data.practitionerTestProduct.id);
      }
    }
  });

  useEffect(() => {
    if (slug) {
      fetchProduct({
        variables: {
          slug,
          paymentIsoCode: basket?.paymentLocale?.isoCode,
          organisation: organisation?.id
        }
      });
    }
  }, [slug, fetchProduct, basket?.paymentLocale?.isoCode, organisation?.id]);

  if (profileLoading && !practitionerProfile) {
    return <Loading />;
  }

  if (practitionerProfile && !practitionerProfile.showReportsTab) {
    return <Redirect to={ERROR_404_URL} />;
  }

  return (
    <>
      <ReportsListContent />
    </>
  );
}
